import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { Spinner, NeonBadge } from './UI';
import { inputStyle, btnPrimary, colors, fonts } from '../lib/styles';

export default function MessagesPage({ currentUser }) {
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => { loadThreads(); }, []);
  useEffect(() => { if (selected) loadMessages(selected); }, [selected]);

  const loadThreads = async () => {
    setLoading(true);
    // Get distinct conversations
    const { data } = await supabase
      .from('messages')
      .select('*, listings(title), sender:profiles!sender_id(handle), recipient:profiles!recipient_id(handle)')
      .or(`sender_id.eq.${currentUser.id},recipient_id.eq.${currentUser.id}`)
      .order('created_at', { ascending: false });

    if (data) {
      // Group by listing_id + other party
      const seen = new Set();
      const unique = [];
      data.forEach(m => {
        const otherId = m.sender_id === currentUser.id ? m.recipient_id : m.sender_id;
        const key = `${m.listing_id}_${otherId}`;
        if (!seen.has(key)) {
          seen.add(key);
          unique.push({ ...m, otherId, otherHandle: m.sender_id === currentUser.id ? m.recipient?.handle : m.sender?.handle });
        }
      });
      setThreads(unique);
    }
    setLoading(false);
  };

  const loadMessages = async (thread) => {
    const { data } = await supabase
      .from('messages')
      .select('*, sender:profiles!sender_id(handle)')
      .eq('listing_id', thread.listing_id)
      .or(`and(sender_id.eq.${currentUser.id},recipient_id.eq.${thread.otherId}),and(sender_id.eq.${thread.otherId},recipient_id.eq.${currentUser.id})`)
      .order('created_at', { ascending: true });
    if (data) setMessages(data);
  };

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    setSending(true);
    await supabase.from('messages').insert({
      listing_id: selected.listing_id,
      sender_id: currentUser.id,
      recipient_id: selected.otherId,
      content: reply.trim(),
    });
    setReply('');
    await loadMessages(selected);
    setSending(false);
  };

  const timeAgo = (ts) => {
    const diff = Date.now() - new Date(ts).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* Thread list */}
      <div style={{
        width: '300px', borderRight: `1px solid ${colors.border}`,
        overflowY: 'auto', flexShrink: 0,
      }}>
        <div style={{ padding: '20px 20px 12px', borderBottom: `1px solid ${colors.border}` }}>
          <h2 style={{ fontFamily: fonts.display, fontSize: '13px', fontWeight: 700, color: colors.cyan, letterSpacing: '0.1em', margin: 0 }}>
            MESSAGES
          </h2>
        </div>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}><Spinner /></div>
        ) : threads.length === 0 ? (
          <div style={{ padding: '40px 20px', fontFamily: fonts.mono, fontSize: '12px', color: colors.textFaint, textAlign: 'center' }}>
            No messages yet.
          </div>
        ) : threads.map((t, i) => (
          <div key={i} onClick={() => setSelected(t)} style={{
            padding: '16px 20px', cursor: 'pointer', borderBottom: `1px solid ${colors.border}`,
            background: selected?.otherId === t.otherId && selected?.listing_id === t.listing_id
              ? 'rgba(0,255,200,0.04)' : 'transparent',
            transition: 'background 0.15s',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span style={{ fontFamily: fonts.display, fontSize: '12px', color: colors.cyan, fontWeight: 700 }}>
                {t.otherHandle || 'unknown'}
              </span>
              <span style={{ fontFamily: fonts.mono, fontSize: '10px', color: colors.textFaint }}>{timeAgo(t.created_at)}</span>
            </div>
            <div style={{ fontFamily: fonts.mono, fontSize: '11px', color: colors.textDim, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              re: {t.listings?.title || 'unknown listing'}
            </div>
            <div style={{ fontFamily: fonts.mono, fontSize: '11px', color: colors.textFaint, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: '3px' }}>
              {t.content}
            </div>
          </div>
        ))}
      </div>

      {/* Message thread */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {!selected ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: fonts.mono, fontSize: '13px', color: colors.textFaint }}>
            Select a conversation
          </div>
        ) : (
          <>
            <div style={{ padding: '16px 24px', borderBottom: `1px solid ${colors.border}`, display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontFamily: fonts.display, fontSize: '13px', fontWeight: 700, color: colors.text }}>{selected.otherHandle}</span>
              <NeonBadge color={colors.textDim}>re: {selected.listings?.title?.slice(0, 40) || 'listing'}</NeonBadge>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {messages.map((m, i) => {
                const isMine = m.sender_id === currentUser.id;
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: isMine ? 'flex-end' : 'flex-start' }}>
                    <div style={{
                      maxWidth: '70%', padding: '10px 14px', borderRadius: '4px',
                      background: isMine ? 'rgba(0,255,200,0.1)' : 'rgba(255,255,255,0.05)',
                      border: `1px solid ${isMine ? '#00ffc830' : colors.border}`,
                    }}>
                      <p style={{ fontFamily: fonts.mono, fontSize: '13px', color: isMine ? colors.cyan : colors.text, margin: '0 0 4px', lineHeight: 1.5 }}>
                        {m.content}
                      </p>
                      <span style={{ fontFamily: fonts.mono, fontSize: '10px', color: colors.textFaint }}>{timeAgo(m.created_at)}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ padding: '16px 24px', borderTop: `1px solid ${colors.border}`, display: 'flex', gap: '10px' }}>
              <input value={reply} onChange={e => setReply(e.target.value)}
                placeholder="Reply..."
                style={{ ...inputStyle, flex: 1 }}
                onKeyDown={e => e.key === 'Enter' && sendReply()} />
              <button onClick={sendReply} disabled={sending || !reply.trim()} style={{ ...btnPrimary(sending || !reply.trim()), padding: '10px 18px' }}>
                {sending ? <Spinner size={14} color="#000" /> : 'SEND'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
